import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { Currency, TsrctDemandAwardingRuleVariant, TsrctLanguage } from '@/generated/gql/graphql';
import {
  termsFormDataDefault,
  termsFormDataSchema,
} from '~/components/NegotiationTermsForm/NegotiationTermsForm.schema';
import { mapToTsrctNegotiationStrategyBoundaryRuleInput } from '~/components/NegotiationTermsForm/NegotiationTermsForm.util';
import { SupplierContactHitId } from '~/components/SupplierContactAutocomplete';
import {
  supplierContactFormDataDefault,
  supplierContactFormDataSchema,
} from '~/modules/NegotiationSupplierContactWidget/components/SupplierContactForm/SupplierContactForm.schema';
import { getDateBoundaries } from '~/util/date/getDateBoundaries';

const lineItemSchema = z.object({
  id: z.string().nullable(),
  position: z.string().min(1).nullable(),
  title: z.string(),
  description: z.string().nullable(),
  commodityGroupId: z.string().nullable(),
  unitId: z.string().min(1),
  quantity: z.number().refine((val) => val > 0),
  pricePerUnit: z.number().min(0),
  earlyDeliveryAt: z.date().nullable(),
  lateDeliveryAt: z.date().nullable(),
});

export const quoteSchema = z.object({
  id: z.string().nullable(),
  currency: z.string(),
  quoteReference: z.string().min(1),
  supplierId: z.string().min(1),
  summary: z.string().nullable(),
  lineItems: z.array(lineItemSchema),
});

export const NegotiationCaseCreateWizardSchema = z
  .object({
    fileId: z.string().min(1),
    demand: z.object({
      id: z.string().min(1),
      awardingRule: z.enum(TsrctDemandAwardingRuleVariant).nullish(),
    }),
    quote: quoteSchema,
    terms: termsFormDataSchema,
  })
  .extend(supplierContactFormDataSchema.shape);

export type NegotiationCaseCreateWizardState = z.infer<typeof NegotiationCaseCreateWizardSchema>;

export const NegotiationCaseCreateWizardDefaultState: DefaultValues<NegotiationCaseCreateWizardState> = {
  fileId: '',
  demand: {
    id: '',
    awardingRule: null,
  },
  quote: {
    id: null,
    currency: DEFAULT_CURRENCY as Currency,
    quoteReference: '',
    supplierId: '',
    summary: null,
    lineItems: [],
  },
  terms: termsFormDataDefault,
  ...supplierContactFormDataDefault,
};

const demandRequestLineItemInputMapper = lineItemSchema.transform(
  ({
    position,
    title,
    description,
    commodityGroupId,
    unitId,
    quantity,
    pricePerUnit,
    earlyDeliveryAt,
    lateDeliveryAt,
  }) => ({
    position: position!,
    title,
    description,
    commodityGroupId,
    unitId,
    quantity: String(quantity),
    expectedPricePerUnit: String(pricePerUnit),
    shipmentTerms: {
      earlyDeliveryAt,
      lateDeliveryAt,
    },
  }),
);

export const toDemandRequestInputMapper = NegotiationCaseCreateWizardSchema.transform(
  ({ demand, quote, supplier }) => ({
    id: demand.id,
    data: {
      summary: quote.summary,
      currency: quote.currency as Currency,
      lineItems: quote.lineItems.map((lineItem) => ({
        ...demandRequestLineItemInputMapper.parse(lineItem),
        preferredSupplierId: supplier.internalId,
      })),
    },
  }),
);

const quoteLineItemInputMapper = lineItemSchema.transform(
  ({ position, title, description, unitId, quantity, pricePerUnit, earlyDeliveryAt, lateDeliveryAt }) => ({
    position: position!,
    title,
    description,
    unitId,
    quantity: String(quantity),
    pricePerUnit: String(pricePerUnit),
    shipmentTerms: {
      earlyDeliveryAt,
      lateDeliveryAt,
    },
  }),
);

export const toNegotiationCaseInputMapper = NegotiationCaseCreateWizardSchema.transform(
  ({ demand, fileId, quote, supplier, terms }) => {
    const { earliest: earlyDeliveryAt, latest: lateDeliveryAt } = getDateBoundaries(quote.lineItems);
    const contact = supplier.contact!;

    return {
      demandId: demand.id,
      data: {
        supplier: {
          ...supplier,
          contact: {
            ...contact,
            internalId: contact.internalId === SupplierContactHitId.NEW ? null : contact.internalId,
            language: contact.language as TsrctLanguage,
          },
        },
        quote: {
          id: quote.id,
          currency: quote.currency as Currency,
          supplierReference: quote.supplierId,
          lineItems: quote.lineItems.map((lineItem) => quoteLineItemInputMapper.parse(lineItem)),
          shipmentTerms: {
            earlyDeliveryAt,
            lateDeliveryAt,
          },
          fileId,
        },
        strategy: {
          boundaryAdjustments: terms.boundaryAdjustments,
          boundaryRules: mapToTsrctNegotiationStrategyBoundaryRuleInput(terms.boundaries),
        },
        options: terms.options,
      },
    };
  },
);
